import BoldSmallText from '@/components/BoldSmallText';
import CustomLayoutGeneralSettings from '@/components/CustomLayoutGeneralSettings';
import InlineWarning from '@/components/InlineWarning';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import SettingToggle from '@/components/settingToggle';
import { optionBannerPlacement } from '@/constants/options';
import { handleToggle, openCrisp } from '@/helpers';
import ModalDynamicThemeList from '@/pages/GeneralSettings/components/DisplayAndPosition/components/ModalDynamicThemeList';
import errorSlice from '@/redux/slice/error.slice';
import { isLoadingGeneralSettingsSelector } from '@/redux/slice/loading';
import settingsSlice, { dataSettingsSelector } from '@/redux/slice/settings.slice';
import toastSlice from '@/redux/slice/toast.slice';
import { Button, Checkbox, InlineGrid, Link, TextField, Tooltip } from '@shopify/polaris';
import { ClipboardIcon, ExternalIcon } from '@shopify/polaris-icons';
import { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { DisplayAndPositionStyled } from './styled';

const DisplayAndPosition = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isLoading = useSelector(isLoadingGeneralSettingsSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const { state: stateLocationURL } = useLocation();
  const dateModeRef = useRef<HTMLDivElement>(null);
  const [isHighlight, setIsHighlight] = useState(false);
  const [isOpenModalThemeList, setIsOpenModalThemeList] = useState(false);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    let highlightTimeout: NodeJS.Timeout;

    if (location.state && location.state.isScrollToApplyItems && dateModeRef.current) {
      scrollTimeout = setTimeout(() => {
        const element = dateModeRef.current as any;
        element.classList.add('highlight-border');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Remove highlight class after 3 seconds
        highlightTimeout = setTimeout(() => {
          element.classList.remove('highlight-border');
        }, 2000);
      }, 1000);
    }

    // Cleanup function to clear timeouts when component unmounts or dependencies change
    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(highlightTimeout);
    };
  }, [location.state]);

  useEffect(() => {
    let idTimeOut: ReturnType<typeof setTimeout>;
    if (stateLocationURL && stateLocationURL.isHighlight) {
      setIsHighlight(true);
      idTimeOut = setTimeout(() => {
        setIsHighlight(false);
      }, 3000);
    }
    return () => {
      clearTimeout(idTimeOut);
    };
  }, [stateLocationURL]);

  useEffect(() => {
    if (!!dataSettings.show_on_line_item && (!dataSettings.cart_page_label_text || !dataSettings.cart_page_date_display)) {
      dispatch(errorSlice.actions.handleIsDisabled(true));
    } else {
      dispatch(errorSlice.actions.handleIsDisabled(false));
    }
  }, [dataSettings.cart_page_date_display, dataSettings.cart_page_label_text, dataSettings.show_on_line_item, dispatch]);

  const handleChange = (key: keyof typeof dataSettings) => (value: string | number) => {
    dispatch(
      settingsSlice.actions.handleDataSettings({
        ...dataSettings,
        [key]: value,
      }),
    );
  };

  const handleChangePlacement = (value: string) => {
    let newBannerPlacement = [...dataSettings.show_on_pages.split(',')];
    if (newBannerPlacement?.includes(value)) {
      newBannerPlacement = newBannerPlacement?.filter((item) => item !== value);
    } else {
      newBannerPlacement.push(value);
    }
    handleChange('show_on_pages')(newBannerPlacement.join(','));
  };
  const handleCopyClick = (text: string) => {
    navigator.clipboard.writeText(text);
    dispatch(
      toastSlice.actions.handleToast({
        content: 'Copied',
        error: false,
        isOpen: true,
      }),
    );
  };

  return (
    <DisplayAndPositionStyled>
      <CustomLayoutGeneralSettings notUseCard title="Activate app">
        <SettingToggle
          title="App status"
          settingToggleProps={{
            enabled: dataSettings?.enable_app === 1,
            action: {
              onAction: () => {
                if (dataSettings) {
                  dispatch(
                    settingsSlice.actions.handleDataSettings({
                      ...dataSettings,
                      enable_app: handleToggle(dataSettings.enable_app),
                    }),
                  );
                }
              },
              loading: isLoading,
            },
          }}
        />
      </CustomLayoutGeneralSettings>

      <div className={`${isHighlight ? 'is-blink-border' : ''}`}>
        <CustomLayoutGeneralSettings
          isShowDivider
          title="Add app on store theme"
          description="Enable and reposition ETA widget on your store in 1 of 3 ways"
        >
          <div className="content-between align-center divider">
            <RegularText>
              <strong>Add app block</strong>
            </RegularText>
            <Button
              icon={ExternalIcon}
              onClick={() => {
                window.open(
                  `https://${dataSettings.shop}.myshopline.com/admin/theme/editor/editing/ProductsDetail?themeId=${dataSettings.themeStoreId}&templateName=templates/product.json&activateAppId=${process.env.REACT_APP_THEME_APP_EXTENSION_ID}/app-block&target=newAppsSection`,
                  '_blank',
                );
              }}
            >
              Add block
            </Button>
          </div>
          <div className="content-between align-center divider mt-8 mb-8">
            <RegularText>
              <strong>Embed app</strong>
            </RegularText>
            <Button
              icon={ExternalIcon}
              onClick={() => {
                window.open(
                  `https://${dataSettings.shop}.myshopline.com/admin/theme/editor/editing/ProductsDetail?themeId=${dataSettings.themeStoreId}&templateName=templates/product.json&activateAppId=${process.env.REACT_APP_THEME_APP_EXTENSION_ID}/app-embed-block`,
                  '_blank',
                );
              }}
            >
              Embed app
            </Button>
          </div>
          <div className="mt-8">
            <RegularText>
              <strong>Manual</strong>
            </RegularText>
            <TextField
              autoComplete="off"
              label={<RegularText>Insert this code snippet to custom HTML at desired position</RegularText>}
              value='<div class="ot-estimated-shipping"></div>'
              suffix={
                <div>
                  <Tooltip content="Copy code">
                    <Button
                      icon={ClipboardIcon}
                      variant="plain"
                      onClick={() => handleCopyClick(`<div class="ot-estimated-shipping"></div>`)}
                    ></Button>
                  </Tooltip>
                </div>
              }
              readOnly
            />
          </div>

          <div className="mt-8">
            <RegularText>
              <i>
                Note: If you use Page Builder, you should both Embed App in Shopline’s Theme Editor and Add custom HTML code in
                Page Builder’s editor.
              </i>
            </RegularText>
          </div>

          <div className="mt-8">
            <RegularText>
              Widget still does not show or find hard to set up?{' '}
              <Link monochrome onClick={openCrisp}>
                Contact us
              </Link>
            </RegularText>
          </div>
        </CustomLayoutGeneralSettings>
      </div>

      <CustomLayoutGeneralSettings
        isShowDivider
        title={'Page'}
        description={<p>Set up where to show the ETA banner on your store</p>}
      >
        <div className="d-flex flex-column gap-8">
          <BoldSmallText>Positions</BoldSmallText>
          <InlineGrid columns={{ xs: 2, sm: 2, md: 4 }} gap={'200'}>
            {optionBannerPlacement.map((item) => {
              return (
                <Checkbox
                  key={item.value}
                  label={item.label}
                  checked={dataSettings.show_on_pages?.includes(item.value)}
                  onChange={() => {
                    handleChangePlacement(item.value);
                  }}
                />
              );
            })}
          </InlineGrid>
          <BoldSmallText>Custom positioning</BoldSmallText>
          <TextField
            label=""
            autoComplete="off"
            value={dataSettings?.custom_position}
            onChange={(value) => {
              if (dataSettings) {
                dispatch(
                  settingsSlice.actions.handleDataSettings({
                    ...dataSettings,
                    custom_position: value,
                  }),
                );
              }
            }}
          />
          <InlineWarning
            iconTone="base"
            textTone="subdued"
            message="Rules with conditions may not display correctly on pages other than the product page due to the limitations to retrieve product properties."
          />
        </div>
      </CustomLayoutGeneralSettings>

      <CustomLayoutGeneralSettings isShowDivider title="Show banner when inventory is not tracked">
        <div className="d-flex align-center justify-between">
          <p>ETA banner will display for product/variant not enabled “Track quantity” in Shopline admin.</p>
          <Switch
            isActive={!!dataSettings?.isShowWhenInventoryNotTracked}
            onSwitch={(value) => {
              if (dataSettings) {
                dispatch(
                  settingsSlice.actions.handleDataSettings({
                    ...dataSettings,
                    isShowWhenInventoryNotTracked: value,
                  }),
                );
              }
            }}
          />
        </div>
      </CustomLayoutGeneralSettings>

      <CustomLayoutGeneralSettings
        isShowDivider
        title={'Custom dynamic date position in cart'}
        // title={'Date mode of delivery estimates in Cart and Checkout'}
        // description={<p>For rules enabling showing text in cart</p>}
      >
        <div ref={dateModeRef}>
          {/* <ChoiceList
            title="Date mode"
            titleHidden={true}
            choices={[
              {
                label: <BoldSmallText>Static date in Cart and Checkout (Default)</BoldSmallText>,
                value: DynamicDateInCartEnum.Static,
                helpText:
                  'Show static date from when product is added to cart. Appear in all cart types, checkout, thank you, and order status page.',
              },
              {
                label: (
                  <div className={`d-flex gap-8 ${isMobile ? 'flex-column' : ''}`}>
                    <BoldSmallText>Dynamic date in Cart</BoldSmallText>
                    <div>
                      <Badge tone="info">Beta</Badge>
                    </div>
                    <Link removeUnderline onClick={() => setIsOpenModalThemeList(true)}>
                      See compatible Shopify themes
                    </Link>
                  </div>
                ),
                value: DynamicDateInCartEnum.Dynamic,
                helpText:
                  'Refresh date based on current day to prevent showing incorrect delivery estimates if buyers abandon cart and return later. Only appear in cart page or drawer, not in checkout.',
              },
            ]}
            selected={dataSettings?.isDynamicDateInCart ? [DynamicDateInCartEnum.Dynamic] : [DynamicDateInCartEnum.Static]}
            onChange={(value) => {
              if (dataSettings) {
                dispatch(
                  settingsSlice.actions.handleDataSettings({
                    ...dataSettings,
                    isDynamicDateInCart: value[0] === DynamicDateInCartEnum.Dynamic ? true : false,
                  }),
                );
              }
            }}
          /> */}

          {/* <div className="mt-8"> */}
          {/* <BoldSmallText>Custom dynamic date position in cart</BoldSmallText> */}
          {/* <div className="mt-4"> */}
          <TextField
            label="Position"
            labelHidden
            value={dataSettings?.customDynamicDateInCart}
            autoComplete="off"
            onChange={(value) => {
              if (dataSettings) {
                dispatch(
                  settingsSlice.actions.handleDataSettings({
                    ...dataSettings,
                    customDynamicDateInCart: value,
                  }),
                );
              }
            }}
          />
          {/* </div> */}
          {/* </div> */}
        </div>
      </CustomLayoutGeneralSettings>

      {/* <CustomLayoutGeneralSettings
        title="Show message text in Email"
        description={<p>Display estimated date in email</p>}
        isShowDivider
      >
        <Link
          url={`https://admin.shopify.com/store/${config?.shop?.replace(
            '.myshopify.com',
            '',
          )}/email_templates/order_confirmation/edit`}
          target="_blank"
          removeUnderline
        >
          <div className="d-flex">
            Open Shopline Email settings
            <Icon source={ExternalIcon} />
          </div>
        </Link>
        <p className="mt-8">
          To show message email, you need to enable in rule setup “Show static delivery estimates in Cart and Checkout”.{' '}
        </p>
        <div>
          Insert this code snippet in email template.{' '}
          <Link url={USER_GUIDE_LINK.SHOW_MESSAGE_TEXT_IN_EMAIL} target="_blank" monochrome>
            See guide
          </Link>
        </div>
        <div className="mt-8">
          <BlockPreviewCode copyContent={emailCodeSnippet}>
            <TextField
              label=""
              autoComplete="off"
              value={emailCodeSnippet}
              multiline
              spellCheck={false}
              maxHeight={115}
              readOnly
            />
          </BlockPreviewCode>
        </div>
        <div className="mt-8">
          <RegularText>
            Find hard to set up?{' '}
            <Link onClick={openCrisp} monochrome>
              Contact us
            </Link>
          </RegularText>
        </div>
      </CustomLayoutGeneralSettings> */}
      <ModalDynamicThemeList isOpenModal={isOpenModalThemeList} setIsOpenModal={setIsOpenModalThemeList} />
    </DisplayAndPositionStyled>
  );
};

export default memo(DisplayAndPosition);
